'use client';
/** @jsxImportSource @emotion/react */

import map from 'lodash/map';
import { FC } from 'react';
import Icon from '../Icon';
import ProgressBar from '../ProgressBar';
import Typography from '../Typography';
import cn from '../Utils/cn';
import Markdown from 'markdown-to-jsx';

interface SkillsLineProps {
	icon: string | null;
	skill: string | null;
	lvl: string | null;
	number: number | null;
	subtitle: string | null;
	list: string[];
	barColor: string;
	compact: boolean;
}

const removeStyles = (text: string) => {
	const t = text?.replace?.(/("|;)\s*font-[^;"]+/gi, '')
		.replace?.(/("|;)\s*color:[^;"]+/gi, '')
		.replace?.(/(\n)+/gi, '\n\n');
	return t;
};

const isHTML = (text: string) => /<([a-z][a-z0-9]*)\b[^>]*>(.*?)<\/\1>/i.test(text);

const SkillsLine: FC<SkillsLineProps> = ({ icon, skill, lvl, number, subtitle, list = [], barColor, compact }) => (
	<div
		className={cn('flex', {
			'mb-12': compact,
			'mb-[4.5rem]': !compact,
		})}
	>
		{icon && (
			<div
				css={(theme: any) => ({
					width: compact ? 25 : 50,
					marginRight: compact ? 25 : 45,
					[theme.breakpoints.down('sm')]: { width: 25, marginRight: 20 },
				})}
			>
				<Icon
					icon={icon}
					className={cn('text-brand-main text-[2.5rem]', {
						'sm:text-[5rem]': !compact,
					})}
				/>
			</div>
		)}

		<div css={{ width: '100%' }}>
			{skill && (
				<Typography variant="headline-6" className="text-xl mb-0">
					{skill}
				</Typography>
			)}
			{number && <ProgressBar progress={number} barColor={barColor} compact={compact} />}
			{lvl && (
				<Typography variant="body-2" className="mb-0">
					{lvl}
				</Typography>
			)}
			{subtitle && (
				<Typography
					variant="headline-6"
					className={cn('mb-0', {
						'mt-4 mb-4': compact,
						'mt-8 mb-8': !compact,
					})}
				>
					{subtitle}
				</Typography>
			)}
			{list?.map((lang, index) => (
				<Typography variant="body-2" key={index} className="mb-0">
					{isHTML(lang) ? (
						<div key={index} dangerouslySetInnerHTML={{ __html: removeStyles(lang) }} className="markdown-css" />
					) : (
						<div key={index} className="markdown-css">
							<Markdown>{removeStyles(lang)}</Markdown>
						</div>
					)}
				</Typography>
			))}
		</div>
	</div>
);

interface SkillsBlockProps {
	knowledges?: { [key: string]: any };
	compact?: boolean;
}

const SkillsBlock: FC<SkillsBlockProps> = ({ knowledges = {}, compact = false }) => {
	if (knowledges == null || Object.values(knowledges).filter(v => v != null).length < 1) {
		return <Typography variant="body-1">Não especificado.</Typography>;
	}

	return (
		<div>
			{map(knowledges, k =>
				k != null && (k.description != null || k.progress > 0) ? (
					<SkillsLine
						key={k.id}
						icon={k.icon}
						skill={k.label}
						lvl={k.description}
						number={k.progress}
						subtitle={k.subtitle}
						list={k.list}
						barColor={k.barColor}
						compact={compact}
					/>
				) : null,
			)}
		</div>
	);
};

export default SkillsBlock;
